// /components/VideoEmbed/VideoEmbed.js
import React, { useEffect } from 'react';
// Import the detectPlatform function as a named export
import { detectPlatform } from '../../utils/detectPlatform';
import { safeExternalUrl } from '../../utils/safeExternalUrl';

// Import the platform-specific embed components
import YouTubeEmbed from './YouTubeEmbed';
import TikTokEmbed from './TikTokEmbed';
import InstagramEmbed from './InstagramEmbed';
import styles from './VideoEmbed.module.css';

/**
 * VideoEmbed Component
 * ----------------------
 * This component receives a URL as a prop, detects which platform the video is from,
 * and renders the appropriate embed component.
 *
 * It also triggers a window resize event on mount (or when the URL changes) so that any
 * parent components (like Tabs or Accordions) can recalc their dimensions.
 *
 * Supported platforms:
 *   - YouTube (and YouTube Shorts)
 *   - TikTok
 *   - Instagram (Reels / Posts)
 *
 * If the platform is unsupported, a fallback message is displayed.
 */
const VideoEmbed = ({ url }) => {
  // Trigger a resize event when the URL changes or the component mounts.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [url]);

  // If no URL is provided, render nothing.
  if (!url) return null;

  // Detect the platform from the URL.
  const platform = detectPlatform(url);
  const externalHref = safeExternalUrl(url);

  // Render the appropriate embed component based on the detected platform.
  switch (platform) {
    case 'youtube':
    case 'youtubeShorts':
      return <YouTubeEmbed url={url} />;
    case 'tiktok':
      return <TikTokEmbed url={url} />;
    case 'instagram':
      return <InstagramEmbed url={url} />;
    default:
      return (
        <div className={styles.unsupported}>
          <p>This link cannot be previewed here, but the original media is still available:</p>
          {externalHref ? (
            <a href={externalHref} target="_blank" rel="noopener noreferrer">{url}</a>
          ) : (
            <span>{url}</span>
          )}
        </div>
      );
  }
};

export default VideoEmbed;
